import styled from '@emotion/styled';

import { clearHistory, formatDate, HistoryEntry, loadHistory } from '~/utils/history';

/* ─── STYLED ─────────────────────────────────────────────────────────────── */
const Panel = styled.div`
  animation: fadeSlideUp var(--transition-slow) ease both;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-surface);
  margin-top: var(--space-lg);
  overflow: hidden;
`;

const PanelHeader = styled.div`
  align-items: center;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  justify-content: space-between;
  padding: var(--space-md) var(--space-lg);
`;

const PanelTitle = styled.span`
  color: var(--color-text-secondary);
  font-family: var(--font-ui);
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-medium);
  letter-spacing: var(--letter-spacing-label);
  text-transform: uppercase;
`;

const ClearBtn = styled.button`
  color: var(--color-text-muted);
  font-family: var(--font-ui);
  font-size: var(--font-size-label);
  letter-spacing: var(--letter-spacing-label);
  text-transform: uppercase;
  transition: color var(--transition-base);

  &:hover {
    color: var(--color-incorrect);
  }
`;

const TableWrap = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
`;

const Th = styled.th`
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text-muted);
  font-family: var(--font-ui);
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-medium);
  letter-spacing: var(--letter-spacing-label);
  padding: var(--space-sm) var(--space-md);
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;

  &:first-of-type {
    padding-left: var(--space-lg);
  }
  &:last-of-type {
    padding-right: var(--space-lg);
  }
`;

const Tr = styled.tr`
  border-bottom: 1px solid var(--color-border);
  transition: background var(--transition-fast);

  &:last-of-type {
    border-bottom: none;
  }

  &:hover {
    background: var(--color-bg-subtle);
  }
`;

const Td = styled.td`
  color: var(--color-text-secondary);
  font-family: var(--font-display);
  font-size: 0.875rem;
  padding: var(--space-sm) var(--space-md);
  white-space: nowrap;

  &:first-of-type {
    padding-left: var(--space-lg);
  }
  &:last-of-type {
    padding-right: var(--space-lg);
  }
`;

const TdHighlight = styled(Td)<{ $color: string }>`
  color: ${p => p.$color};
  font-weight: var(--font-weight-medium);
`;

const DiffBadge = styled.span<{ $diff: string }>`
  background: ${p =>
    p.$diff === 'easy'
      ? 'rgba(52,211,153,0.1)'
      : p.$diff === 'hard'
        ? 'rgba(248,113,113,0.1)'
        : 'rgba(129,140,248,0.1)'};
  border: 1px solid
    ${p =>
      p.$diff === 'easy'
        ? 'rgba(52,211,153,0.3)'
        : p.$diff === 'hard'
          ? 'rgba(248,113,113,0.3)'
          : 'rgba(129,140,248,0.3)'};
  border-radius: var(--radius-sm);
  color: ${p =>
    p.$diff === 'easy'
      ? 'var(--color-correct)'
      : p.$diff === 'hard'
        ? 'var(--color-incorrect)'
        : 'var(--color-wpm)'};
  font-size: 0.65rem;
  letter-spacing: var(--letter-spacing-label);
  padding: 2px 7px;
  text-transform: uppercase;
`;

const EmptyState = styled.div`
  color: var(--color-text-muted);
  font-family: var(--font-ui);
  font-size: var(--font-size-label);
  letter-spacing: var(--letter-spacing-label);
  padding: var(--space-xl) var(--space-lg);
  text-align: center;
  text-transform: uppercase;
`;

function getRatingLabel(wpm: number): string {
  if (wpm >= 120) return 'Elite';
  if (wpm >= 100) return 'Excellent';
  if (wpm >= 80) return 'Fast';
  if (wpm >= 60) return 'Proficient';
  if (wpm >= 40) return 'Average';
  if (wpm >= 20) return 'Beginner';
  return 'Starter';
}

/* ─── COMPONENT ─────────────────────────────────────────────────────────── */
interface HistoryPanelProps {
  onClose: () => void;
  refreshKey: number;
}

export default function HistoryPanel({ onClose, refreshKey }: HistoryPanelProps) {
  const history: HistoryEntry[] = loadHistory();

  const handleClear = () => {
    clearHistory();
    onClose();
  };

  return (
    <Panel key={refreshKey}>
      <PanelHeader>
        <PanelTitle>Last {history.length} test{history.length !== 1 ? 's' : ''}</PanelTitle>
        <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
          {history.length > 0 && <ClearBtn onClick={handleClear}>Clear</ClearBtn>}
          <ClearBtn onClick={onClose} style={{ color: 'var(--color-text-muted)' }}>
            &#x2715;
          </ClearBtn>
        </div>
      </PanelHeader>

      {history.length === 0 ? (
        <EmptyState>No tests yet — complete a test to see history</EmptyState>
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th>Date</Th>
                <Th>WPM</Th>
                <Th>Raw</Th>
                <Th>Accuracy</Th>
                <Th>Time</Th>
                <Th>Mode</Th>
                <Th>Rating</Th>
              </tr>
            </thead>
            <tbody>
              {history.map(entry => (
                <Tr key={entry.id}>
                  <Td style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
                    {formatDate(entry.date)}
                  </Td>
                  <TdHighlight $color="var(--color-wpm)">{entry.wpm}</TdHighlight>
                  <Td>{entry.rawWpm}</Td>
                  <TdHighlight $color="var(--color-accuracy)">{entry.accuracy}%</TdHighlight>
                  <Td style={{ color: 'var(--color-time)' }}>{entry.timeMode}s</Td>
                  <Td>
                    <DiffBadge $diff={entry.difficulty}>{entry.difficulty}</DiffBadge>
                  </Td>
                  <Td style={{ color: 'var(--color-text-secondary)' }}>
                    {getRatingLabel(entry.wpm)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </Panel>
  );
}
